import { useState } from "react";
import { Search, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../app-layout";
import PageHeader from "@/components/fragments/page-header";
import PageContainer from "@/components/fragments/page-container";
import NotificationCard from "@/components/cards/notification-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from "@/redux/services/notifications/notificationsApi";
import type { Notification } from "@/types/notifications";
import { toast } from "sonner";
import routesPath from "@/utils/routesPath";
import { formatDateTime } from "@/utils/helpers";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("all");

  // API hooks
  const {
    data: notificationsResponse,
    isLoading,
    error,
    refetch,
  } = useGetNotificationsQuery({});

  const [markAllAsRead, { isLoading: isMarkingAllAsRead }] =
    useMarkAllNotificationsAsReadMutation();
  const [markAsRead] = useMarkNotificationAsReadMutation();

  // Extract notifications from API response
  const notifications: Notification[] = notificationsResponse?.data || [];

  // Helper function to format timestamp
  const formatTimestamp = (dateString: string) =>
    formatDateTime(dateString, "en-GB", "N/A");

  // Helper function to extract title from message
  const extractTitle = (message: string) => {
    // Extract product name from message if it exists
    const productMatch = message.match(/\*\*(.*?)\*\*/);
    if (productMatch) {
      return `RFQ for ${productMatch[1]}`;
    }
    return "New Request for Quote";
  };

  const handleRespondToRequest = async (notification: Notification) => {
    try {
      // Mark notification as read by sending PATCH request
      const res = await markAsRead(notification.id).unwrap();
      if (res?.success === true) {
        // Refresh the notifications list
        refetch();
      }
    } catch (error: any) {
      console.error("Failed to mark notification as read:", error);
      // Continue navigation even if marking as read fails
    }

    // Navigate to dashboard (RFQ listing page)
    navigate(routesPath.DASHBOARD);
  };

  const handleMarkAsRead = async () => {
    try {
      const res = await markAllAsRead(undefined).unwrap();
      if (res?.success === true) {
        toast.success(res?.message || "All notifications marked as read");
        refetch(); // Refresh the notifications list
      } else {
        toast.error(res?.message || "Failed to mark notifications as read");
      }
    } catch (error: any) {
      console.error("Failed to mark all as read:", error);
      toast.error(
        (error as any)?.data?.message || "Failed to mark notifications as read"
      );
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    const title = extractTitle(notification.data.message);
    const description = notification.data.message;

    const matchesSearch =
      title.toLowerCase().includes(searchValue?.toLowerCase()) ||
      description.toLowerCase().includes(searchValue?.toLowerCase());

    const matchesFilter =
      filterValue === "all" ||
      (filterValue === "unread" && !notification.read_at) ||
      (filterValue === "read" && notification.read_at);

    return matchesSearch && matchesFilter;
  });

  return (
    <DashboardLayout>
      <section className="w-full h-full overflow-y-auto space-y-4">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <PageHeader
              title="Notification"
              mobileTitle="Notifications"
              description="Listed below are your bidding history"
              mobileDescription="Your notification history"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAsRead}
            disabled={isMarkingAllAsRead || isLoading}
            className="border-primary text-primary hover:bg-primary/10 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMarkingAllAsRead ? "Marking..." : "Mark all as read"}
          </Button>
        </div>

        {/* Search and Filter Section */}
        <div className="flex justify-between items-center px-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-10 border-gray-200 bg-white"
              id="search"
              name="search"
            />
          </div>

          <Select value={filterValue} onValueChange={setFilterValue}>
            <SelectTrigger className="w-32 border-gray-200 bg-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Notifications List */}
        <PageContainer className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">Failed to load notifications</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          ) : filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                timestamp={formatTimestamp(notification.created_at)}
                title={extractTitle(notification.data.message)}
                description={notification.data.message}
                actionText="Respond to Request"
                onActionClick={() => handleRespondToRequest(notification)}
                isRead={!!notification.read_at}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No notifications found</p>
            </div>
          )}
        </PageContainer>
      </section>
    </DashboardLayout>
  );
}
